import { Card, CardContent, Typography, Box, Divider } from '@mui/material';
import { ImageGrid } from './image-grid';
import type { DefectLineResponse } from '@core/types/ticket';

interface DefectLineDisplayProps {
  line: DefectLineResponse;
  lineNumber: number;
}

export function DefectLineDisplay({ line, lineNumber }: DefectLineDisplayProps) {
  const lineTypeLabel = line.line_type === 'Machine' ? 'Lỗi máy' : 'Lỗi linh kiện';

  const componentDisplay = (() => {
    if (line.line_type !== 'Component') return null;
    if (line.is_other_component) {
      return `${line.other_component_description || '—'} (tự nhập)`;
    }
    const name = line.component_item_name || line.component_item_code || '—';
    const code = line.component_item_code ? ` (${line.component_item_code})` : '';
    return line.component_item_name ? `${name}${code}` : name;
  })();

  const defectDisplay = (() => {
    if (line.is_other_defect) {
      return `${line.other_defect_description || '—'} (tự nhập)`;
    }
    const name = line.defect_name || line.market_defect || '—';
    const code = line.market_defect && line.defect_name ? ` (${line.market_defect})` : '';
    return `${name}${code}`;
  })();

  const causeDisplay = (() => {
    if (line.is_other_cause) {
      return `${line.other_cause_description || '—'} (tự nhập)`;
    }
    const name = line.cause_name || line.market_defect_cause || '—';
    const code = line.market_defect_cause && line.cause_name ? ` (${line.market_defect_cause})` : '';
    return `${name}${code}`;
  })();

  return (
    <Card sx={{ mb: 2 }} id={`defect-line-${lineNumber}`}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          🔧 Lỗi #{lineNumber}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <DetailRow label="Loại" value={lineTypeLabel} />

          {componentDisplay && (
            <DetailRow label="Linh kiện" value={componentDisplay} />
          )}

          <DetailRow label="Lỗi" value={defectDisplay} />
          <DetailRow label="Nguyên nhân" value={causeDisplay} />
        </Box>

        {line.images && line.images.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              📷 Hình ảnh ({line.images.length}):
            </Typography>
            <ImageGrid images={line.images} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
        {label}:
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}
